<template>
  <div v-if="showTriggerLink || relatedJobNumbers.length" class="links-panel">
    <div v-if="relatedJobNumbers.length" class="related-jobs" data-test="related-jobs">
      <v-icon small>mdi-link-variant</v-icon>
      <span class="related-jobs__label">Related Jobs:</span>
      <router-link
        v-for="jobNumber in visibleRelatedJobs"
        :key="jobNumber"
        :to="`/jobs/${jobNumber}`"
        class="related-jobs__link"
      >
        #{{ jobNumber }}
      </router-link>
      <button
        v-if="hiddenRelatedJobsCount"
        type="button"
        class="related-jobs__more"
        data-test="related-jobs-more"
        @click="showAllRelatedJobs = !showAllRelatedJobs"
      >
        {{ showAllRelatedJobs ? 'Show less' : `+${hiddenRelatedJobsCount} more` }}
      </button>
    </div>
    <vs-button
      v-if="showTriggerLink"
      small
      type="tertiary"
      right-icon
      icon="mdi-open-in-new"
      label="View Trigger"
      :href="triggerUrl"
      target="_blank"
      data-test="tank-trigger-link"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import VsButton from '@/components/vision/VsButton.vue'
import { useGetters } from 'vuex-composition-helpers'

interface Props {
  triggerOrganizationId: string
  triggerUrl: string
  relatedJobNumbers?: string[]
}
const props = withDefaults(defineProps<Props>(), {
  relatedJobNumbers: () => [],
})

// You need to be an admin and belong to the org which owns the trigger
const { getOrganizationId: currentUserOrgId, hasAccountOrganizationAccess } = useGetters(
  'permissions',
  ['getOrganizationId', 'hasAccountOrganizationAccess'],
)

const hasAdminPortalScadaAccess = computed(() => {
  return (
    hasAccountOrganizationAccess.value && currentUserOrgId.value === props.triggerOrganizationId
  )
})

const showTriggerLink = computed(() => hasAdminPortalScadaAccess.value && props.triggerUrl)

const MAX_VISIBLE_RELATED_JOBS = 2
const showAllRelatedJobs = ref(false)

const visibleRelatedJobs = computed(() =>
  showAllRelatedJobs.value
    ? props.relatedJobNumbers
    : props.relatedJobNumbers.slice(0, MAX_VISIBLE_RELATED_JOBS),
)
const hiddenRelatedJobsCount = computed(() =>
  Math.max(props.relatedJobNumbers.length - MAX_VISIBLE_RELATED_JOBS, 0),
)
</script>

<style scoped>
.related-jobs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.related-jobs__label {
  font-size: 14px;
  color: var(--color-grey);
}

.related-jobs__link {
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
}

.related-jobs__link:hover {
  text-decoration: underline;
}

.related-jobs__more {
  font-size: 14px;
  font-weight: 700;
  color: var(--color-grey);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.related-jobs__more:hover {
  text-decoration: underline;
}

.links-panel {
  font-family: var(--font-regular);
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(6, 95, 212, 0.1);
  gap: 12px 24px;
  flex-wrap: wrap;
  justify-content: space-between;
}
</style>
